<template>
  <button
    ref="amountInput"
    :disabled="disabled"
    class="h-fit w-fit relative flex items-center border rounded border-grays-400 px-[4px] hover:border-grays-600 disabled:!border-grays-400 disabled:text-grays-600"
  >
    <input
      :class="versionMini ? 'h-[32px] text-sm' : 'h-[32px] text-base'"
      :disabled="disabled"
      :value="validModelValue"
      class="w-[73px] font-bold text-center !appearance-none outline-none bg-transparent"
      type="number"
      @blur="restoreBorderColor"
      @focus="changeBorderColor"
      @input="updateInput($event)"
    />
    <button
      v-if="validModelValue < maxAmount"
      :disabled="disabled"
      class="group absolute top-0 bottom-0 right-[4px] m-auto h-[16px] w-[16px] flex"
      @click="increaseAmount"
    >
      <span
        class="material-symbols-rounded text-grays-700 text-base leading-[16px] hover:text-grays-900 active:text-grays-900 group-disabled:text-grays-600"
        >add</span
      >
    </button>
    <button
      v-if="validModelValue > 1"
      :disabled="disabled"
      class="group absolute top-0 bottom-0 left-[4px] m-auto h-[16px] w-[16px] flex"
      @click="decreaseAmount"
    >
      <span
        class="material-symbols-rounded text-grays-700 text-base leading-[16px] hover:text-grays-900 active:text-grays-900 group-disabled:text-grays-600"
        >remove</span
      >
    </button>
  </button>
</template>

<script>
import { mapGetters } from "vuex";

// The cap is read straight off the `maxAmount` prop. It used to be mirrored
// into a data field that a watcher and the updated() hook refreshed, which
// left the mirror one render behind the prop. A sold-out line's cap is the
// amount the order already holds, so when the cart merge raised both the
// amount and the cap in one render (1 -> 3), the modelValue watcher below ran
// first, clamped 3 against the stale cap of 1, and the card showed 1 next to
// a cart that said 3.
export default {
  name: "input-amount",
  props: {
    versionMini: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxAmount: {
      type: Number,
      default: 100,
      required: false,
    },
    modelValue: [Number, String],
    outOfStock: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters({
      noLimit: "user/getNoLimit",
    }),
    validModelValue() {
      const value = parseInt(this.modelValue);
      return isNaN(value) || value <= 0
        ? 1
        : value > this.maxAmount
        ? this.maxAmount
        : value;
    },
  },
  watch: {
    modelValue(newValue) {
      const parsedValue = parseInt(newValue);
      if (parsedValue <= 0) {
        this.$emit("update:modelValue", 1);
      } else if (parsedValue > this.maxAmount) {
        this.$emit("update:modelValue", this.maxAmount);
      }
    },
  },
  methods: {
    updateInput(event) {
      const value = event.target.value;
      if (value !== null && value.toString().length > 0) {
        let parsedValue = parseInt(value);
        if (isNaN(parsedValue) || parsedValue <= 0) {
          parsedValue = 1;
        } else if (parsedValue > this.maxAmount) {
          parsedValue = this.maxAmount;
        }
        event.target.value = parsedValue;
        this.$emit("update:modelValue", parsedValue);
        this.$emit("valueChanged");
      }
    },
    decreaseAmount(event) {
      event.stopPropagation();
      const currentValue = parseInt(this.modelValue);
      if (currentValue > 1) {
        this.$emit("update:modelValue", currentValue - 1);
        this.$emit("valueChanged");
      }
    },
    increaseAmount(event) {
      event.stopPropagation();
      const currentValue = parseInt(this.validModelValue);
      if (currentValue < this.maxAmount) {
        this.$emit("update:modelValue", currentValue + 1);
        this.$emit("valueChanged");
      }
    },
    changeBorderColor() {
      this.$refs.amountInput.style.borderColor = "#484848";
    },
    changeBorderColorHover() {
      this.$refs.amountInput.style.borderColor = "#ACACAC";
    },
    restoreBorderColor() {
      this.$refs.amountInput.style.borderColor = "";
    },
  },
};
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
  /* Firefox */
}
</style>
